import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Paperclip, X, Send, CheckCircle2, RotateCcw, FileText } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { StatusPill, SUPPORT_CATEGORY_LABELS, SUPPORT_PRIORITY_STYLES } from "@/pages/seller/Support";

const MAX_BYTES = 5 * 1024 * 1024;
const errDetail = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fb;
};

export const AttachmentChips = ({ items, side = "left" }) => (
  (items || []).length > 0 ? (
    <div className={`mt-2 flex flex-wrap gap-2 ${side === "right" ? "justify-end" : ""}`}>
      {items.map((a, i) => (
        <a key={i} href={a.url} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 rounded bg-white/70 border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-white">
          <FileText className="w-3 h-3" /> <span className="truncate max-w-[140px]">{a.filename}</span>
        </a>
      ))}
    </div>
  ) : null
);

export const MessageThread = ({ messages, myRole }) => (
  <div className="space-y-4">
    {(messages || []).map((m) => {
      const mine = m.author_role === myRole;
      const internal = m.internal;
      return (
        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
          <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm ${
            internal ? "bg-amber-50 border border-amber-200 text-amber-900"
              : mine ? "bg-brand text-white" : "bg-slate-100 text-slate-800"}`}>
            <div className={`flex items-center gap-2 text-xs mb-1 ${mine && !internal ? "text-white/80" : "text-slate-500"}`}>
              <span className="font-semibold">{internal ? "Internal note" : m.author_name}</span>
              <span>·</span><span>{formatToIST(m.created_at)}</span>
            </div>
            <div className="whitespace-pre-wrap break-words">{m.body}</div>
            <AttachmentChips items={m.attachments} side={mine ? "right" : "left"} />
          </div>
        </div>
      );
    })}
  </div>
);

const SupportDetail = ({ user, onLogout }) => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [acting, setActing] = useState(false);
  const bottomRef = useRef(null);

  const fetchTicket = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/support/tickets/${ticketId}`);
      setTicket(data);
    } catch (e) {
      toast.error(errDetail(e, "Failed to load the ticket"));
      navigate("/seller/support");
    } finally {
      setLoading(false);
    }
  }, [ticketId, navigate]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [ticket?.messages?.length]);

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []).filter((f) => {
      if (f.size > MAX_BYTES) { toast.error(`${f.name} exceeds the 5 MB limit`); return false; }
      return true;
    });
    setFiles((prev) => [...prev, ...picked].slice(0, 5));
    e.target.value = "";
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("body", reply.trim());
      files.forEach((f) => fd.append("attachments", f));
      const { data } = await axios.post(`${API}/support/tickets/${ticketId}/reply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTicket(data);
      setReply(""); setFiles([]);
    } catch (e) {
      toast.error(errDetail(e, "Could not send the reply"));
    } finally {
      setSending(false);
    }
  };

  const doAction = async (path, okMsg) => {
    setActing(true);
    try {
      const { data } = await axios.post(`${API}/support/tickets/${ticketId}/${path}`);
      setTicket(data);
      toast.success(okMsg);
    } catch (e) {
      toast.error(errDetail(e, "Action failed"));
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return <SellerLayout user={user} onLogout={onLogout}><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></SellerLayout>;
  }
  if (!ticket) return null;

  const closed = ticket.status === "CLOSED";
  const resolved = ticket.status === "RESOLVED";

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-5 w-full max-w-3xl">
        <PageHeader
          title={ticket.ticket_number}
          subtitle={ticket.subject}
          backTo="/seller/support"
          badge={<StatusPill status={ticket.status} />}
          actions={
            <div className="flex gap-2">
              {resolved && <Button variant="outline" onClick={() => doAction("close", "Ticket closed")} disabled={acting}><CheckCircle2 className="w-4 h-4 mr-1.5" /> Close</Button>}
              {(resolved || closed) && <Button variant="outline" onClick={() => doAction("reopen", "Ticket reopened")} disabled={acting}><RotateCcw className="w-4 h-4 mr-1.5" /> Reopen</Button>}
            </div>
          }
        />

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-5">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <span><span className="text-slate-500">Category:</span> <span className="font-medium text-slate-800">{SUPPORT_CATEGORY_LABELS[ticket.category] || ticket.category}</span></span>
              <span><span className="text-slate-500">Priority:</span> <span className={`font-medium capitalize ${SUPPORT_PRIORITY_STYLES[ticket.priority] || ""}`}>{ticket.priority}</span></span>
              {ticket.order_number && <span><span className="text-slate-500">Order:</span> <span className="font-medium text-slate-800">{ticket.order_number}</span></span>}
              {ticket.awb && <span><span className="text-slate-500">AWB:</span> <span className="font-mono text-slate-700">{ticket.awb}</span></span>}
              <span className="text-slate-400">Raised {formatToIST(ticket.created_at)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-5">
            <MessageThread messages={ticket.messages} myRole="seller" />
            <div ref={bottomRef} />
          </CardContent>
        </Card>

        {closed ? (
          <Card className="border-0 shadow-sm bg-slate-50">
            <CardContent className="py-5 text-center text-sm text-slate-500">
              This ticket is closed. <button className="text-brand font-medium hover:underline" onClick={() => doAction("reopen", "Ticket reopened")} disabled={acting}>Reopen</button> to continue the conversation.
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-5 space-y-3">
              <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply…" />
              {files.length > 0 && (
                <ul className="space-y-1">
                  {files.map((f, i) => (
                    <li key={i} className="flex items-center justify-between rounded bg-slate-50 px-3 py-1.5 text-sm">
                      <span className="truncate max-w-[260px]">{f.name}</span>
                      <button type="button" onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex items-center justify-between">
                <label className="inline-flex items-center gap-1.5 cursor-pointer text-sm text-slate-600 hover:text-slate-800">
                  <Paperclip className="w-4 h-4" /> Attach
                  <input type="file" multiple accept=".pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={onFiles} />
                </label>
                <Button onClick={sendReply} disabled={sending || !reply.trim()}>{sending ? "Sending…" : <><Send className="w-4 h-4 mr-1.5" /> Send</>}</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </SellerLayout>
  );
};

export default SupportDetail;
